/**
 * Weather, seen (core/weather.ts is the sim's seeded timeline; 62.18 made sight, perception and
 * hearing follow it, and nothing drew it). Two cheap parts, presentation only:
 *
 *   - **the air**: the sky haze's fog is driven to the weather's sight range (a person fades out at
 *     the range the sim stops seeing them — what you can't see, the bot can't either) and the sky
 *     tints with the kind (grey rain, white fog, ochre dust, dark ash); changes ease over a few
 *     seconds so a new segment is a change in the weather, not a cut;
 *   - **the fall**: rain and storm as thin streaks, dust and ash as drifting flecks — one InstancedMesh
 *     of a few hundred pieces in a box round the camera that moves with it, the pieces cycling
 *     through the box by time. Only inside an atmosphere (the orbit never rains).
 *
 * 67.36: in a strong wind the ground gives up grit, snow or leaves (by its material, 63.5) that blow along it — in the
 * fall's own InstancedMesh, after the fall's pieces, so wind adds no draw. `buildFall` and `placeFall` are the one code
 * path the game's view and the wind sheet (design_sheets/wind.html) both run.
 *
 * Nothing in the sim; off whenever the match has no weather. `weatherLook` is pure
 * (tests/weatherView.test.ts).
 */
import * as THREE from 'three';
import type { GameState, PlanetState } from '../types';
import { weatherHere, planetWeather, WEATHER_EFFECTS, type WeatherKind } from '../core/weather';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { getSurfaceRadius } from '../core/surface';
import { insideAtmosphere, type SkyWeather } from './skyHaze';
import { airOf } from '../core/air';
import { setCloudWind } from './cloudLayer';
import { markShared } from './meshBake';
import { groundMaterialAt } from '../core/groundMaterial';

export interface WeatherLook {
  kind: WeatherKind;
  /** the sight range in world units, or Infinity (clear) */
  sight: number;
  /** the range the drawing fades the world over (world units): the sim's sight, or further for a kind drawn lighter (DRAWN_REACH — 389: haze at 2 ×); Infinity clear */
  drawn: number;
  /** the sky's tint under this weather and how far toward it the sky goes (0 = the planet's own sky) */
  tint: string;
  tintAmount: number;
  /** the fall: none, or pieces of a kind, their count, fall speed (person-heights a second), drift and length (person-heights) */
  fall: null | { shape: 'streak' | 'fleck'; count: number; speed: number; drift: number; length: number; color: string; opacity: number };
}

const LOOKS: Record<WeatherKind, Omit<WeatherLook, 'kind' | 'sight' | 'drawn'>> = {
  clear: { tint: '#ffffff', tintAmount: 0, fall: null },
  haze: { tint: '#d8d4c8', tintAmount: 0.35, fall: null },
  rain: { tint: '#8c94a0', tintAmount: 0.6, fall: { shape: 'streak', count: 700, speed: 28, drift: 3, length: 0.3, color: '#dde6ee', opacity: 0.45 } },
  snow: { tint: '#e6eaf0', tintAmount: 0.55, fall: { shape: 'fleck', count: 500, speed: 3, drift: 4, length: 0.07, color: '#f4f7fb', opacity: 0.7 } },   // 74.11 (a): slow white flecks, a pale sky
  fog: { tint: '#dcdedd', tintAmount: 0.8, fall: null },
  storm: { tint: '#5c6470', tintAmount: 0.8, fall: { shape: 'streak', count: 1000, speed: 40, drift: 9, length: 0.45, color: '#d0dae4', opacity: 0.5 } },
  dust: { tint: '#c9a86a', tintAmount: 0.65, fall: { shape: 'fleck', count: 400, speed: 2, drift: 8, length: 0.06, color: '#d8bb86', opacity: 0.5 } },
  ash: { tint: '#6e6a66', tintAmount: 0.7, fall: { shape: 'fleck', count: 350, speed: 1.2, drift: 2, length: 0.08, color: '#9a948e', opacity: 0.6 } },
};

/**
 * 389 (the owner, on 1 % fog at the Story's spawn: "I see more fog, or haze rather than 1% I think"): haze held the place a
 * fifth of the minutes and its scene fog closed the world at its sight (~63 people) in fog's grey — it read as fog. A kind
 * here draws lighter than it sees: the scene fog and tint under it end at this × its sight (haze 2 ×: 60 → 120 people); the
 * sim's sight rule is untouched (the person still fades out of the bots' sight at 60). Fog, rain, snow and storm keep their bands.
 */
export const DRAWN_REACH: Partial<Record<WeatherKind, number>> = { haze: 2 };

/** What `kind` looks like at this person height (the sight range comes from the sim's table, in person-lengths; the drawn range is it × DRAWN_REACH). */
export function weatherLook(kind: WeatherKind, person: number): WeatherLook {
  const sight = WEATHER_EFFECTS[kind].sight;
  const s = Number.isFinite(sight) ? sight * person : Infinity;
  return { kind, sight: s, drawn: s * (DRAWN_REACH[kind] ?? 1), ...LOOKS[kind] };
}

/** The fog band for a sight range: a person is fully faded by the range the sim stops seeing them, and half gone at a third of it. */
export function fogForSight(sight: number): { near: number; far: number } | null {
  return Number.isFinite(sight) ? { near: sight * 0.35, far: sight * 1.05 } : null;
}

/** The box the fall fills round the camera (person-heights): half-width, its height, and the hole round the lens (a piece that close reads as a pillar). */
export const FALL_BOX = { half: 12, height: 14, hole: 1.5 };
/** Seconds a weather change takes to ease in. */
export const WEATHER_EASE_S = 3;
/** Clear air's stand-in sight for the ease: eight times the longest finite range (the fog rolls in over a few seconds, not twenty). */
export function clearSight(person: number): number { return 8 * 60 * person; }

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _q = new THREE.Quaternion(), _s = new THREE.Vector3(1, 1, 1), _up = new THREE.Vector3(), _fwd = new THREE.Vector3(), _right = new THREE.Vector3();
const _Y = new THREE.Vector3(0, 1, 0), _zero = new THREE.Vector3(0, 0, 0);
const _slant = new THREE.Vector3(), _qSlant = new THREE.Quaternion();   // 62.52: the streaks' slant in the wind
const _dir = new THREE.Vector3(), _side = new THREE.Vector3(), _ground = new THREE.Vector3(), _sd = new THREE.Vector3(), _c = new THREE.Color(), _e = new THREE.Euler();
const _b = new THREE.Vector3();

/**
 * DECISIONS 207 (7)'s follow-up (the owner, riding in: *"weather happens inside (it's raining inside)"*): a box the fall never
 * enters — the ride-in cabin's inside — as `toBox` (world → the box's frame: unscaled, its centre at the origin) and its
 * half-extents there (world units). A host puts it on the camera (`camera.userData.shelter`) while the camera sits in it; the
 * walls already hide what falls behind them (the pieces are depth-tested), so with the ramp down the weather shows through the
 * opening only. A piece whose centre is within its length of the box is parked, so no streak pokes in through the roof.
 */
export interface FallShelter { toBox: THREE.Matrix4; half: THREE.Vector3 }
const sheltered = (p: THREE.Vector3, sh: FallShelter | null, reach: number): boolean => {
  if (!sh) return false;
  _b.copy(p).applyMatrix4(sh.toBox);
  return Math.abs(_b.x) < sh.half.x + reach && Math.abs(_b.y) < sh.half.y + reach && Math.abs(_b.z) < sh.half.z + reach;
};

/** 67.36: what a strong wind lifts off the ground, by what the ground is. */
export type DriftKind = 'grit' | 'snow' | 'leaves';
/** The ground's drift: how many pieces at most, the wind's level (0–1 against a storm's) they start at and are all up by, the band they blow in over the ground and how fast (person-heights), the hole round the lens. */
export const DRIFT = { count: 160, from: 0.3, full: 0.8, band: 1.2, slow: 4, fast: 18, hole: 1.0 };
const DRIFT_LOOK: Record<DriftKind, { colors: string[]; size: [number, number] }> = {
  grit: { colors: ['#c8b48a', '#a89c84', '#8a8580'], size: [0.04, 0.04] },
  snow: { colors: ['#f4f8fc', '#e4ecf4'], size: [0.05, 0.05] },
  leaves: { colors: ['#6a8a3a', '#8a6a2a', '#a8842e', '#5a7a34'], size: [0.11, 0.06] },
};
/** What blows off a ground material (63.5's): leaves off soil and mud, snow off snow and ice, grit off the rest. */
export function driftFor(material: string | null): DriftKind | null {
  if (!material) return null;
  return material === 'soil' || material === 'mud' ? 'leaves' : material === 'snow' || material === 'ice' ? 'snow' : 'grit';
}
/** The ground's drift where the camera is: what blows, the wind's level (0–1 against a storm), the camera's height over the ground and the planet's radius. */
export interface GroundDrift { kind: DriftKind; level: number; alt: number; radius: number }
const smooth = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/**
 * The fall's mesh for a look (null: the look has no fall): one InstancedMesh of the kind's streak or fleck, room after
 * the fall's pieces for the ground's drift (67.36), each piece coloured (the fall's in its look's colour), and its seeds.
 */
export function buildFall(look: WeatherLook, person: number): { mesh: THREE.InstancedMesh; seeds: Float32Array } | null {
  if (!look.fall) return null;
  const f = look.fall, n = f.count + DRIFT.count;
  const geometry = f.shape === 'streak'
    ? new THREE.PlaneGeometry(0.015 * person, f.length * person)         // a hair-thin vertical streak
    : new THREE.PlaneGeometry(f.length * person, f.length * person);     // a small fleck
  const material = markShared(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: f.opacity, depthWrite: false, side: THREE.DoubleSide, fog: false }));
  const mesh = new THREE.InstancedMesh(geometry, material, n);
  mesh.name = `weather:${f.shape}`;
  mesh.frustumCulled = false;
  mesh.userData.noBake = true;
  mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(n * 3), 3);
  _c.set(f.color);
  for (let i = 0; i < f.count; i++) mesh.setColorAt(i, _c);
  mesh.count = f.count;
  const seeds = new Float32Array(n * 4);
  let s = 1234567;
  const r = () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  for (let i = 0; i < n; i++) { seeds[i * 4] = r(); seeds[i * 4 + 1] = r(); seeds[i * 4 + 2] = r(); seeds[i * 4 + 3] = r(); }
  return { mesh, seeds };
}

/**
 * Every piece: a point in the box round the camera, cycling down (or drifting, for flecks) with time; streaks face the
 * camera. 62.52: with `wind` (the way it blows along the ground × its factor) the fall drifts the way the wind blows and
 * the streaks slant along their path; without it, today's drift across the view. 67.36: then, with `drift`, the
 * ground's grit, snow or leaves blowing downwind in a band over the ground (curving with the planet), more as the wind
 * rises past DRIFT.from.
 */
export function placeFall(mesh: THREE.InstancedMesh, seeds: Float32Array, look: WeatherLook, person: number, time: number, camera: THREE.Camera, up: THREE.Vector3, wind: { x: number; y: number; z: number } | null, drift: GroundDrift | null = null, shelter: FallShelter | null = null): void {
  const f = look.fall!, reach = (f.length * person) / 2;
  camera.getWorldDirection(_fwd);
  _right.crossVectors(_fwd, up).normalize();
  const fwd = _fwd.clone().crossVectors(up, _right).normalize();   // the facing, flattened to the ground plane
  const half = FALL_BOX.half * person, height = FALL_BOX.height * person;
  const fallen = (time * f.speed * person) / height, driftX = (time * f.drift * person) / (2 * half);
  const wobble = f.shape === 'fleck' ? 0.6 : 0.1, hole = FALL_BOX.hole * person;
  // 62.52: with the wind on, the fall drifts the way the wind blows (along the ground, at the kind's drift × the wind's
  // factor) and the streaks slant along their path; without it, today's drift across the view
  const w = wind;
  const wr = w ? w.x * _right.x + w.y * _right.y + w.z * _right.z : 0, wf = w ? w.x * fwd.x + w.y * fwd.y + w.z * fwd.z : 0;
  const driftR = w ? (time * f.drift * person * wr) / (2 * half) : driftX * 0.5, driftF = w ? (time * f.drift * person * wf) / (2 * half) : 0;
  if (w && f.shape === 'streak') {
    _slant.copy(up).multiplyScalar(-f.speed).addScaledVector(_right, f.drift * wr).addScaledVector(fwd, f.drift * wf).normalize().negate();
    _qSlant.setFromUnitVectors(_Y, _slant);
  }
  for (let i = 0; i < f.count; i++) {
    const sx = seeds[i * 4], sy = seeds[i * 4 + 1], sz = seeds[i * 4 + 2], ph = seeds[i * 4 + 3];
    const y = ((sy - fallen + ph) % 1 + 1) % 1;                                   // falls, wrapping through the box
    const x = ((sx + driftR + wobble * 0.05 * Math.sin(time * 1.7 + ph * 6.28)) % 1 + 1) % 1;
    const z = ((sz + driftF + wobble * 0.04 * Math.cos(time * 1.3 + ph * 6.28)) % 1 + 1) % 1;
    _p.copy(camera.position).addScaledVector(_right, (x - 0.5) * 2 * half).addScaledVector(fwd, (z - 0.5) * 2 * half).addScaledVector(up, (y - 0.5) * height);
    const near = _p.distanceTo(camera.position) < hole || sheltered(_p, shelter, reach);   // inside the hole (or the shelter): parked at zero size this frame
    // streaks hang along the ground's up and turn to face the camera; flecks tumble
    if (f.shape === 'streak') (w ? _q.copy(_qSlant) : _q.setFromUnitVectors(_Y, up)).multiply(new THREE.Quaternion().setFromAxisAngle(_Y, Math.atan2(_fwd.x, _fwd.z)));
    else _q.setFromEuler(new THREE.Euler(time * 0.8 + ph * 6, time * 0.5 + ph * 3, 0));
    mesh.setMatrixAt(i, _m.compose(_p, _q, near ? _zero : _s));
  }
  // 67.36: the ground's drift, after the fall's pieces in the same mesh
  let active = 0;
  if (w && drift && drift.level > DRIFT.from && Math.hypot(w.x, w.y, w.z) > 1e-6) {
    const d = DRIFT_LOOK[drift.kind], g = (mesh.geometry as THREE.PlaneGeometry).parameters;
    const level = Math.min(1, drift.level), speed = (DRIFT.slow + (DRIFT.fast - DRIFT.slow) * level) * person;
    active = Math.min(seeds.length / 4 - f.count, Math.round(DRIFT.count * smooth(DRIFT.from, DRIFT.full, level)));
    _dir.set(w.x, w.y, w.z).addScaledVector(up, -(w.x * up.x + w.y * up.y + w.z * up.z)).normalize();
    _side.crossVectors(up, _dir).normalize();
    _ground.copy(camera.position).addScaledVector(up, -drift.alt);
    _sd.set((d.size[0] * person) / g.width, (d.size[1] * person) / g.height, 1);   // the kind's size, from the fall's quad
    for (let j = 0; j < active; j++) {
      const i = f.count + j, s0 = seeds[i * 4], s1 = seeds[i * 4 + 1], s2 = seeds[i * 4 + 2], s3 = seeds[i * 4 + 3];
      const along = (((s0 + (time * speed) / (2 * half)) % 1) + 1) % 1;
      const pa = (along - 0.5) * 2 * half, pc = (s1 - 0.5) * 2 * half;
      const hop = (0.05 + s2 * DRIFT.band) * person * (0.45 + 0.55 * Math.abs(Math.sin(time * (2 + 3 * s3) + s3 * 6.28)));
      _p.copy(_ground).addScaledVector(_dir, pa).addScaledVector(_side, pc).addScaledVector(up, hop - (pa * pa + pc * pc) / (2 * drift.radius));
      const near = _p.distanceTo(camera.position) < DRIFT.hole * person || sheltered(_p, shelter, (d.size[1] * person) / 2);
      _q.setFromEuler(_e.set(time * (3 + s3 * 4) + s0 * 6, time * 2 + s1 * 6, s2 * 6));
      mesh.setMatrixAt(i, _m.compose(_p, _q, near ? _zero : _sd));
      mesh.setColorAt(i, _c.set(d.colors[j % d.colors.length]));
    }
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }
  mesh.count = f.count + active;
  mesh.instanceMatrix.needsUpdate = true;
}

export class WeatherView {
  private kind: WeatherKind | null = null;
  private fall: THREE.InstancedMesh | null = null;
  private fallSeeds: Float32Array | null = null;   // per piece: x, y, z offsets in the box (0–1) and a phase
  private readonly sky: SkyWeather = { tint: new THREE.Color('#ffffff'), tintAmount: 0, sight: Infinity };
  private readonly target: SkyWeather = { tint: new THREE.Color('#ffffff'), tintAmount: 0, sight: Infinity };
  private time = 0;
  /** the eased drawn range as a plain number; clear is a finite stand-in (clearSight) so an ease toward fog settles in a few seconds */
  private sightEased = -1;
  /** what the sound follows: the weather on the planet under the camera (null = none), and whether the camera is inside its air */
  kindNow: WeatherKind | null = null;
  inAir = false;
  /** 62.47: the planet the view is over (−1 none), for what reads the wind at the viewer. */
  planetIndex = -1;
  /**
   * 62.52: the wind at the camera with decision 120's switch on — the way it blows along the ground (world, unit) × its
   * factor (the gust × the shelter: 1 the weather's usual wind) — or null: the fall then drifts as it always has.
   * The game sets it each drawn frame (main.ts, from core/wind.ts).
   */
  wind: { x: number; y: number; z: number } | null = null;

  constructor(private readonly scene: THREE.Scene) {}

  /**
   * Each frame: the weather on the planet nearest the camera, eased, handed to the sky haze as
   * `SkyWeather`; the fall drawn inside the atmosphere. `actionSeconds` is the action clock's time
   * (0 on the turn clock, which keys weather by round).
   */
  update(state: GameState, camera: THREE.Camera, actionSeconds: number, dt: number): SkyWeather | null {
    this.time += dt;
    if (!state.config.weather) { this.clear(); this.kindNow = null; this.inAir = false; setCloudWind(null, camera.position, null); return null; }
    // the planet under the camera
    let pi = -1, gap = Infinity;
    state.planets.forEach((p, i) => { if (p.type !== 'planet' || p.alive === false) return; const g = Math.hypot(camera.position.x - p.position.x, camera.position.y - p.position.y, camera.position.z - p.position.z) - p.radius; if (g < gap) { gap = g; pi = i; } });
    this.planetIndex = pi;
    if (pi < 0) { this.clear(); this.kindNow = null; this.inAir = false; setCloudWind(null, camera.position, null); return null; }
    const planet = state.planets[pi];
    setCloudWind(planet, camera.position, this.wind);   // 67.47: the clouds overhead drift with the wind here (last frame's: the game sets it after this)
    const camDir = { x: camera.position.x - planet.position.x, y: camera.position.y - planet.position.y, z: camera.position.z - planet.position.z };
    const kind = weatherHere(state, pi, camDir, actionSeconds) ?? 'clear';   // 62.54: the weather where the camera is
    this.kindNow = kind;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    const look = weatherLook(kind, person);
    if (kind !== this.kind) { this.kind = kind; this.target.tint.set(look.tint); this.target.tintAmount = look.tintAmount; this.target.sight = look.drawn; this.rebuildFall(look, person); }   // (389: the drawn range, haze's past its sight)
    // ease toward the target (the sight eases in log space, so clear ↔ fog is one smooth pull)
    const k = Math.min(1, dt / WEATHER_EASE_S);
    this.sky.tint.lerp(this.target.tint, k);
    this.sky.tintAmount += (this.target.tintAmount - this.sky.tintAmount) * k;
    const clear = clearSight(person);
    if (this.sightEased < 0) this.sightEased = clear;
    const a = Math.log(this.sightEased), b = Math.log(Math.min(this.target.sight, clear));
    this.sightEased = Math.exp(a + (b - a) * k);
    this.sky.sight = this.sightEased >= 0.9 * clear ? Infinity : this.sightEased;
    // inside the air? (the fall and the sound both stop at the edge of it)
    const rel = { x: camera.position.x - planet.position.x, y: camera.position.y - planet.position.y, z: camera.position.z - planet.position.z };
    const dist = Math.hypot(rel.x, rel.y, rel.z);
    _up.set(rel.x / dist, rel.y / dist, rel.z / dist);
    const alt = dist - getSurfaceRadius(planet, { x: _up.x, y: _up.y, z: _up.z });
    this.inAir = insideAtmosphere(alt, planet.radius, airOf(planet).rimOverR) > 0.05;
    // the fall, inside the atmosphere only, riding the camera
    if (this.fall && this.fallSeeds) {
      this.fall.visible = this.inAir;
      if (this.fall.visible) placeFall(this.fall, this.fallSeeds, look, person, this.time, camera, _up, this.wind, this.groundDrift(state, planet, kind, alt), (camera.userData.shelter as FallShelter | undefined) ?? null);   // (207 (7): none inside the ride-in cabin)
    }
    return this.sky;
  }

  private rebuildFall(look: WeatherLook, person: number): void {
    if (this.fall) { this.scene.remove(this.fall); this.fall.geometry.dispose(); this.fall.dispose(); this.fall = null; this.fallSeeds = null; }
    const built = buildFall(look, person);
    if (!built) return;
    this.fall = built.mesh; this.fallSeeds = built.seeds;
    this.scene.add(this.fall);
  }

  /** 67.36: what the ground under the camera gives up in this wind, or null (no wind, too light, or the switch off). */
  private groundDrift(state: GameState, planet: PlanetState, kind: WeatherKind, alt: number): GroundDrift | null {
    const w = this.wind;
    if (!w) return null;
    const level = (Math.hypot(w.x, w.y, w.z) * WEATHER_EFFECTS[kind].wind) / WEATHER_EFFECTS.storm.wind;   // the factor against a storm's wind
    if (level <= DRIFT.from) return null;
    const drift = driftFor(groundMaterialAt(planet, { x: _up.x, y: _up.y, z: _up.z }));
    return drift ? { kind: drift, level, alt, radius: planet.radius } : null;
  }

  private clear(): void {
    this.kind = null;
    if (this.fall) { this.scene.remove(this.fall); this.fall.geometry.dispose(); this.fall.dispose(); this.fall = null; this.fallSeeds = null; }
    this.target.tintAmount = 0; this.target.sight = Infinity;
    this.sky.tintAmount = 0; this.sky.sight = Infinity; this.sightEased = -1;
  }

  dispose(): void { this.clear(); }
}
